import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, Slider, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import RotateRightOutlinedIcon from "@mui/icons-material/RotateRightOutlined";

import getCroppedImg from "../../helpers/cropImage";
import SoftBox from "../SoftBox";
import SoftButton from "../SoftButton";
import SoftTypography from "../SoftTypography";

const HubLogoCropper = ({ imageSrc, open, onClose, onComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleDone = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSaving(true);
    try {
      const file = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      onComplete?.(file);
      onClose?.();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: "#fff",
          border: "1px solid #EAECF0",
          boxShadow:
            "0px 8px 8px rgba(16, 24, 40, 0.04), 0px 20px 24px rgba(16, 24, 40, 0.1)",
          maxHeight: "calc(100vh - 32px)",
          overflow: "hidden",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          background: "rgba(16, 24, 40, 0.5)",
        },
      }}
    >
      <SoftBox sx={{ p: { xs: 2, sm: 2.5 } }}>
        <SoftBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <SoftBox>
            <SoftTypography
              variant="h5"
              sx={{ color: "#101828", fontWeight: 600, fontSize: "20px" }}
            >
              Adjust logo
            </SoftTypography>
            <SoftTypography
              variant="caption"
              sx={{ color: "#667085", display: "block", mt: 0.5 }}
            >
              Drag to reposition · zoom and rotate to fit the square frame
            </SoftTypography>
          </SoftBox>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "#667085",
              border: "1px solid #D0D5DD",
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </SoftBox>

        <SoftBox
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 240, sm: 320 },
            borderRadius: "12px",
            overflow: "hidden",
            background: "#101828",
            border: "1px solid #D0D5DD",
          }}
        >
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              rotation={rotation}
              zoom={zoom}
              aspect={1}
              cropShape="rect"
              showGrid
              onCropChange={setCrop}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          )}
        </SoftBox>

        <SoftBox sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
          <SoftBox>
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <ZoomInOutlinedIcon sx={{ color: "#667085", fontSize: 18 }} />
              <SoftTypography variant="caption" sx={{ color: "#475467", fontWeight: 600 }}>
                Zoom
              </SoftTypography>
            </SoftBox>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.05}
              onChange={(_, value) => setZoom(value)}
              sx={{
                color: "primary.main",
                "& .MuiSlider-rail": { opacity: 0.3 },
              }}
            />
          </SoftBox>
          <SoftBox>
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <RotateRightOutlinedIcon sx={{ color: "#667085", fontSize: 18 }} />
              <SoftTypography variant="caption" sx={{ color: "#475467", fontWeight: 600 }}>
                Rotate
              </SoftTypography>
            </SoftBox>
            <Slider
              value={rotation}
              min={0}
              max={360}
              step={1}
              onChange={(_, value) => setRotation(value)}
              sx={{
                color: "primary.main",
                "& .MuiSlider-rail": { opacity: 0.3 },
              }}
            />
          </SoftBox>
        </SoftBox>

        <SoftBox
          sx={{
            display: "flex",
            gap: 1.5,
            mt: 3,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <SoftButton
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: "none",
              color: "#344054",
              borderColor: "#D0D5DD",
              backgroundColor: "#fff",
              minWidth: 100,
              "&:hover": {
                backgroundColor: "#F9FAFB",
                borderColor: "#98A2B3",
              },
            }}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="primary"
            onClick={handleDone}
            disabled={saving}
            sx={{ textTransform: "none", minWidth: 100, color: "#fff" }}
          >
            {saving ? "Saving…" : "Done"}
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </Dialog>
  );
};

export default HubLogoCropper;
